import { type IsdDistrictProgramEnhancement } from '../models/IsdDistrictProgramEnhancement'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import {
  type UpdateIsdDistrictProgramEnhancementFormValues
} from '../pages/pageComponents/planning/priorityActivities/priorityFour/IsdDistrictProgramEnhancementModal'
import { GetIsdDistrictProgramEnhancements, LockActivity, UnlockActivity } from '../services/AnnualPlanService'
import { PriorityFourActivityName } from '../models/PriorityFourActivityName'
import { handleApiResponse } from '../utils/apiHelpers'

interface UseIsdDistrictProgramEnhancementsType {
  isdDistrictProgramEnhancements: IsdDistrictProgramEnhancement[]
  isLoading: boolean
  reloadIsdDistrictProgramEnhancements: () => Promise<void>
  error: string | null
}

export const useIsdDistrictProgramEnhancements = (priorityId: number, implementationPhase?: boolean): UseIsdDistrictProgramEnhancementsType => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isdDistrictProgramEnhancements, setIsdDistrictProgramEnhancements] = useState<IsdDistrictProgramEnhancement[]>([])
  const [error, setError] = useState<string | null>(null)

  const loadIsdDistrictProgramEnhancements = async (): Promise<void> => {
    try {
      setIsLoading(true)

      const enhancements = await handleApiResponse(GetIsdDistrictProgramEnhancements(priorityId, implementationPhase !== true))

      if (enhancements != null) setIsdDistrictProgramEnhancements(enhancements)
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'An unknown error occurred while fetching ISD/District Program Enhancements.')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void loadIsdDistrictProgramEnhancements()
  }, [])

  return {
    error,
    isLoading,
    isdDistrictProgramEnhancements,
    reloadIsdDistrictProgramEnhancements: loadIsdDistrictProgramEnhancements
  }
}

interface UseIsdDistrictProgramEnhancementModalType {
  handleAddIsdDistrictProgramEnhancement: () => void
  handleEditIsdDistrictProgramEnhancement: (isdDistrictProgramEnhancement: IsdDistrictProgramEnhancement) => Promise<void>
  handleCloseModal: () => Promise<void>
  showIsdDistrictProgramEnhancementModal: boolean
  initialValues?: UpdateIsdDistrictProgramEnhancementFormValues
  handleTrackProgramEnhancement: (isdDistrictProgramEnhancement: IsdDistrictProgramEnhancement) => Promise<void>
  handleCloseTrackingModal: () => Promise<void>
  programEnhancementToTrack: IsdDistrictProgramEnhancement | null
  programEnhancementTrackingToDelete: IsdDistrictProgramEnhancement | null
  setProgramEnhancementTrackingToDelete: Dispatch<SetStateAction<IsdDistrictProgramEnhancement | null>>
}

export const useIsdDistrictProgramEnhancementModal = (): UseIsdDistrictProgramEnhancementModalType => {
  const [initialValues, setInitialValues] = useState<UpdateIsdDistrictProgramEnhancementFormValues>()
  const [showIsdDistrictProgramEnhancementModal, setShowIsdDistrictProgramEnhancementModal] = useState(false)
  const [programEnhancementToTrack, setProgramEnhancementToTrack] = useState<IsdDistrictProgramEnhancement | null>(null)
  const [programEnhancementTrackingToDelete, setProgramEnhancementTrackingToDelete] = useState<IsdDistrictProgramEnhancement | null>(null)

  const handleAddIsdDistrictProgramEnhancement = (): void => {
    setInitialValues(undefined)
    setShowIsdDistrictProgramEnhancementModal(true)
  }

  const handleEditIsdDistrictProgramEnhancement = async (isdDistrictProgramEnhancement: IsdDistrictProgramEnhancement): Promise<void> => {
    setInitialValues({
      ...isdDistrictProgramEnhancement,
      organizationId: isdDistrictProgramEnhancement.organization.id,
      isUpdate: isdDistrictProgramEnhancement.isUpdate
    })
    setShowIsdDistrictProgramEnhancementModal(true)
    await LockActivity(
      isdDistrictProgramEnhancement.id,
      PriorityFourActivityName.ProgramEnrichment,
      isdDistrictProgramEnhancement.isUpdate
    )
  }

  const handleCloseModal = async (): Promise<void> => {
    setShowIsdDistrictProgramEnhancementModal(false)
    if (initialValues != null) {
      await UnlockActivity(
        initialValues.id,
        PriorityFourActivityName.ProgramEnrichment,
        initialValues.isUpdate
      )
    }
  }

  const handleTrackProgramEnhancement = async (isdDistrictProgramEnhancement: IsdDistrictProgramEnhancement): Promise<void> => {
    setProgramEnhancementToTrack(isdDistrictProgramEnhancement)

    await LockActivity(
      isdDistrictProgramEnhancement.id,
      PriorityFourActivityName.ProgramEnrichment,
      isdDistrictProgramEnhancement.isUpdate
    )
  }

  const handleCloseTrackingModal = async (): Promise<void> => {
    setProgramEnhancementToTrack(null)

    if (programEnhancementToTrack != null) {
      await UnlockActivity(
        programEnhancementToTrack.id,
        PriorityFourActivityName.ProgramEnrichment,
        programEnhancementToTrack.isUpdate
      )
    }
  }

  return {
    initialValues,
    handleCloseModal,
    showIsdDistrictProgramEnhancementModal,
    handleAddIsdDistrictProgramEnhancement,
    handleEditIsdDistrictProgramEnhancement,
    programEnhancementToTrack,
    handleTrackProgramEnhancement,
    handleCloseTrackingModal,
    programEnhancementTrackingToDelete,
    setProgramEnhancementTrackingToDelete
  }
}
